export interface ReportStreamResult {
  [key: string]: any;
  answer: string;
  requestId?: string;
  rateLimit?: { remaining: number; resetTime: number; resetTimeText: string };
}

// Keep incomplete UTF-8 characters and SSE frames between network reads.
export async function readReportStream(
  body: ReadableStream<Uint8Array>,
  onText: (answer: string) => void,
  onEvent?: (payload: Record<string, any>) => void,
): Promise<ReportStreamResult> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let answer = '';
  let result: ReportStreamResult | undefined;
  const consume = (frame: string) => {
    const data = frame.split('\n').filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trimStart()).join('\n');
    if (!data) return; // Includes heartbeat comments.
    const payload = JSON.parse(data);
    onEvent?.(payload);
    if (payload.type === 'error') {
      throw new Error([payload.error, payload.details, payload.requestId && `请求 ID：${payload.requestId}`].filter(Boolean).join('；'));
    }
    if (payload.type === 'content' && typeof payload.text === 'string') {
      answer += payload.text;
      onText(answer);
    }
    if (payload.type === 'replace' && typeof payload.text === 'string') {
      answer = payload.text;
      onText(answer);
    }
    if (payload.type === 'done') {
      result = { ...payload, answer: payload.answer ?? answer };
    }
  };
  try {
    while (!result) {
      const { done, value } = await reader.read();
      buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
      // Normalize after appending so a CRLF split across reads remains intact.
      buffer = buffer.replace(/\r\n/g, '\n');
      let boundary: number;
      while (!result && (boundary = buffer.indexOf('\n\n')) !== -1) {
        consume(buffer.slice(0, boundary));
        buffer = buffer.slice(boundary + 2);
      }
      if (done) {
        if (!result && buffer.trim()) consume(buffer);
        break;
      }
    }
    if (!result || !answer.trim()) throw new Error('报告接收中断，当前内容尚未完整生成，请返回重试。');
    return result;
  } finally {
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}
